/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo } from "react";
import {
  EuiListGroup,
  EuiListGroupItem,
  EuiPopover,
} from "@inscopix/ideas-eui";
import { RESOURCE_TIERS_BY_ID, ResourceTier } from "types/ResourceTiers";
import { isDefined } from "utils/isDefined";
import { ToolSpec } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { formatToolResources } from "components/ToolParamsGrid/GridEditorResourceTierChoice";
import { useDataTableContext } from "../store/DataTableProvider";

const styles = {
  popover: css`
    & {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      padding: 0px 11px;
    }
  `,
  panel: css`
    max-height: max(200px, 50vh);
    overflow-y: scroll;
  `,
};

interface CellEditorComputeResourcesProps {
  rowId: string;
  toolVersionResources: ToolSpec["resources"];
  toolVersionCredits: ToolSpec["credits"];
  resourceTierId: ResourceTier["id"] | null;
  stopEditing: (cancel?: boolean) => void;
}

/** Cell editor for cells in the compute resources column of an analysis table */
const CellEditorComputeResources = ({
  rowId,
  toolVersionCredits,
  toolVersionResources,
  resourceTierId,
  stopEditing,
}: CellEditorComputeResourcesProps) => {
  const selectedTableId = useDataTableContext((s) => s.selectedTableId);
  const updateAnalysisTableRow = useDataTableContext(
    (s) => s.updateAnalysisTableRow,
  );

  return (
    <EuiPopover
      anchorPosition="downCenter"
      button={
        <span>
          {formatToolResources(
            {
              resources: toolVersionResources,
              credits: toolVersionCredits,
            },
            resourceTierId,
          )}
        </span>
      }
      isOpen
      panelPaddingSize="none"
      css={styles.popover}
      closePopover={stopEditing}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          stopEditing(true);
        }
      }}
      display="block"
      repositionOnScroll={false}
    >
      <EuiListGroup css={styles.panel} maxWidth="100%">
        {Object.values(RESOURCE_TIERS_BY_ID)
          .filter(isDefined)
          .map((tier) => (
            <EuiListGroupItem
              id={tier.id.toString()}
              key={tier.id}
              iconType={tier.id === resourceTierId ? "check" : "empty"}
              label={formatToolResources(
                {
                  resources: toolVersionResources,
                  credits: toolVersionCredits,
                },
                tier.id,
              )}
              onClick={() => {
                if (selectedTableId !== undefined) {
                  void updateAnalysisTableRow({
                    tableId: selectedTableId,
                    rowId,
                    resourceTierId: tier.id === resourceTierId ? null : tier.id,
                  });
                  stopEditing();
                }
              }}
            />
          ))}
      </EuiListGroup>
    </EuiPopover>
  );
};

export const CellEditorComputeResourcesMemo = memo(CellEditorComputeResources);
